import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Search,
  Plus,
  RefreshCw,
  Download,
  Mail,
  Phone,
  Building2,
  X,
  Loader,
  Save,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  UserX,
  UserCheck,
  MoreVertical,
  Eye,
  EyeOff,
  Upload,
  Globe
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import { sanitizeInput } from '../../utils/sanitize';
import useGlobalSearch from '../../hooks/useGlobalSearch';

// Common Components
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Badge from '../../components/Common/Badge';

const CollegeRecruiter = ({ isEmbedded = false, forcedTab = null }) => {
  const navigate = useNavigate();
  const modalFileRef = useRef(null);
  const { user: currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState(forcedTab || 'smaart'); // 'smaart' or 'our'
  useEffect(() => { if (forcedTab) setActiveTab(forcedTab); }, [forcedTab]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewUser, setViewUser] = useState(null);

  // ── Smaart Recruiter modal (legacy /api/users flow) ──────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '', email: '', mobile: '', password: '', confirmPassword: '',
    role: 'recruiter', status: 'active', qualification: '', profileImage: '', college: '', website: ''
  });

  // ── Our Recruiter (/api/college-recruiters) ───────────────────────────────
  const [ourRecruiters, setOurRecruiters] = useState([]);
  const [ourLoading, setOurLoading] = useState(false);
  const [ourStatus, setOurStatus] = useState('');
  const [ourPage, setOurPage] = useState(1);
  const [ourTotalPages, setOurTotalPages] = useState(1);
  const [isOurModalOpen, setIsOurModalOpen] = useState(false);
  const [ourSaving, setOurSaving] = useState(false);
  const ourFileRef = useRef(null);
  const [ourFormData, setOurFormData] = useState({ companyName: '', website: '', profileImage: '' });

  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400, () => {
    setCurrentPage(1);
    setOurPage(1);
  });



  const fetchOurRecruiters = async () => {
    try {
      setOurLoading(true);
      const params = { page: ourPage, limit: 10, ...(debouncedSearch && { search: debouncedSearch }), ...(ourStatus && { status: ourStatus }) };
      const { data } = await api.get('/college-recruiters', { params });
      if (data.success) { setOurRecruiters(data.data); setOurTotalPages(data.totalPages); }
    } catch (err) { toast.error('Failed to fetch recruiters'); }
    finally { setOurLoading(false); }
  };

  useEffect(() => { if (activeTab === 'our') fetchOurRecruiters(); }, [activeTab, ourPage, debouncedSearch, ourStatus]);

  const handleOurSubmit = async (e) => {
    e.preventDefault();
    if (!ourFormData.companyName) { toast.error('Company name is required'); return; }
    try {
      setOurSaving(true);
      const { data } = await api.post('/college-recruiters', ourFormData);
      if (data.success) {
        toast.success('Recruiter added successfully');
        setIsOurModalOpen(false);
        setOurFormData({ companyName: '', website: '', profileImage: '' });
        fetchOurRecruiters();
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to add recruiter'); }
    finally { setOurSaving(false); }
  };

  const handleOurStatusChange = async (id, newStatus) => {
    if (!window.confirm(`Change status to ${newStatus}?`)) return;
    try {
      const { data } = await api.put(`/college-recruiters/${id}/status`, { status: newStatus });
      if (data.success) { toast.success('Status updated'); fetchOurRecruiters(); }
    } catch { toast.error('Failed to update status'); }
  };

  const handleOurDelete = async (id) => {
    if (!window.confirm('Delete this recruiter?')) return;
    try {
      const { data } = await api.delete(`/college-recruiters/${id}`);
      if (data.success) { toast.success('Recruiter deleted'); fetchOurRecruiters(); }
    } catch { toast.error('Failed to delete recruiter'); }
  };

  useEffect(() => {
    const collegeId = currentUser?.college?._id || currentUser?.college;
    if (collegeId) {
      setFormData(prev => ({
        ...prev,
        college: collegeId
      }));
    }
  }, [currentUser]);



  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
    fetchUsers();
  }, [activeTab]);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearch, statusFilter, currentPage, sortBy, sortOrder]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const collegeId = currentUser?.college?._id || currentUser?.college;

      const params = {
        page: currentPage,
        limit: 10,
        role: 'recruiter',
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
        sortBy,
        sortOrder
      };

      const { data } = await api.get('/users', { params });

      if (data.success) {
        // Client-side filter based on tab:
        // 'smaart' tab: u.college is null, undefined, or missing
        // 'our' tab: u.college matches current user's college
        const filtered = data.data.filter(u => {
          const recCollegeId = u.college?._id || u.college;
          if (activeTab === 'smaart') {
            return !recCollegeId;
          } else {
            return recCollegeId && recCollegeId === collegeId;
          }
        });

        setUsers(filtered);
        // Approximate pages based on filtered results
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch recruiters:', error);
      toast.error('Failed to fetch recruiters');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const confirmed = window.confirm(`Are you sure you want to change status to ${newStatus}?`);
    if (!confirmed) return;

    try {
      const { data } = await api.put(`/users/${id}/status`, { status: newStatus });
      if (data.success) {
        toast.success(`Recruiter status updated to ${newStatus}`);
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this recruiter? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const { data } = await api.delete(`/users/${id}`);
      if (data.success) {
        toast.success('Recruiter deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete recruiter');
    }
  };

  const handleExport = () => {
    const exportData = users.map(u => ({
      'User ID': sanitizeInput(u.userId || 'N/A'),
      'Full Name': sanitizeInput(u.fullName || 'N/A'),
      'Email': sanitizeInput(u.email),
      'Mobile': sanitizeInput(u.mobile),
      'Company': sanitizeInput(u.qualification || 'N/A'),
      'Status': sanitizeInput(u.status),
      'Joined': new Date(u.createdAt).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recruiters');
    XLSX.writeFile(wb, `${activeTab}_recruiters_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${exportData.length} recruiter(s)`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.qualification) {
      toast.error('Company / Organization is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      const { confirmPassword, ...submitData } = formData;
      submitData.fullName = formData.qualification;

      // Auto-generate required user account fields
      const cleanCompany = formData.qualification.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const randomId = Math.random().toString(36).substring(2, 8);
      submitData.email = `${cleanCompany}_${randomId}@smaartcollege.com`;
      submitData.mobile = '0000000000';
      submitData.password = `Recruiter_${randomId}@123`;

      const { data } = await api.post('/users', submitData);
      if (data.success) {
        toast.success('Recruiter added successfully');
        setIsModalOpen(false);
        const collegeId = currentUser?.college?._id || currentUser?.college;
        setFormData({
          fullName: '',
          email: '',
          mobile: '',
          password: '',
          confirmPassword: '',
          role: 'recruiter',
          status: 'active',
          qualification: '',
          profileImage: '',
          college: collegeId,
          website: ''
        });
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding recruiter:', error);
      toast.error(error.response?.data?.message || 'Failed to add recruiter');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={isEmbedded ? "space-y-6 font-sans" : "min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-[#00152E]/5 p-4 lg:p-6 space-y-6 font-sans"}>
      {/* Header */}
      {!isEmbedded && (
        <div className="glass rounded-xl shadow-md border border-white/30 p-4 mb-6 animate-fade-in">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2.5 bg-white dark:bg-[#002147] text-gray-650 dark:text-gray-300 hover:text-[var(--placement-primary)] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[var(--placement-primary-hover)] rounded-xl transition-all border border-gray-200 dark:border-[var(--placement-primary-border)]/45 group shadow-sm flex items-center justify-center shrink-0"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1">
                  Recruiter Directory
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-650 dark:text-gray-300">
                  <span className="font-medium">View corporate hiring partners and manage recruiters</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                icon={RefreshCw}
                onClick={fetchUsers}
                size="sm"
                className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 hover:border-[var(--placement-primary)]/50"
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant="outline"
                icon={Download}
                onClick={handleExport}
                size="sm"
                className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 hover:border-[var(--placement-primary)]/50"
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
              {activeTab === 'our' && (
                <button
                  onClick={() => navigate('/college/add-recruiter')}
                  className="flex items-center gap-2 text-sm px-4 py-2 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white font-semibold rounded-xl transition-all shadow-md shadow-[var(--placement-primary-border)] hover:shadow-lg hover:shadow-[var(--placement-primary-border)] transform hover:-translate-y-0.5 transition-colors"
                >
                  <Plus size={14} />
                  <span className="hidden sm:inline">Add Recruiter</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Filters */}
      <div className="bg-white dark:bg-[#001e42] rounded-[1.25rem] p-4 shadow-sm border border-gray-200 dark:border-[#1a3884]/30 flex flex-col sm:flex-row items-center gap-4">
        <div className="w-full sm:flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 w-4 h-4 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-[#001430] border border-gray-200/50 dark:border-[#1a3884]/30 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent transition-all font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600"
          />
        </div>
        {isEmbedded && (
          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0 justify-end">
            <Button
              variant="outline"
              icon={RefreshCw}
              onClick={fetchUsers}
              className="h-[46px] px-5 rounded-xl border-gray-200 hover:bg-gray-50 text-gray-600 dark:border-[#1a3884]/30 dark:hover:bg-[#1a3884]/10 dark:text-gray-300 transition-colors"
            >
              <span className="font-semibold text-sm">Refresh</span>
            </Button>
            <Button
              variant="outline"
              icon={Download}
              onClick={handleExport}
              className="h-[46px] px-5 rounded-xl border-gray-200 hover:bg-gray-50 text-gray-600 dark:border-[#1a3884]/30 dark:hover:bg-[#1a3884]/10 dark:text-gray-300 transition-colors"
            >
              <span className="font-semibold text-sm">Export</span>
            </Button>
            {activeTab === 'our' && (
              <button
                onClick={() => navigate('/college/add-recruiter')}
                className="h-[46px] flex items-center gap-2 px-5 bg-[#1a3884] hover:bg-[#0f225c] text-white rounded-xl text-sm font-bold shadow-md shadow-[#1a3884]/20 transition-all active:scale-95 shrink-0"
              >
                <Plus size={16} />
                <span>Add Recruiter</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Cards Grid */}
      {(activeTab === 'our' ? ourLoading : loading) ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--placement-primary)]"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading recruiters...</p>
        </div>
      ) : (activeTab === 'our' ? ourRecruiters : users).length === 0 ? (
        <div className="bg-white/70 dark:bg-[#002147]/80 backdrop-blur-md rounded-2xl p-20 text-center border border-gray-100/50 dark:border-[var(--placement-primary-border)]/35">
          <div className="flex flex-col items-center">
            <div className="p-4 bg-gray-50 dark:bg-[#001f40]/60 rounded-full mb-4">
              <Briefcase className="w-12 h-12 text-gray-400 dark:text-gray-300" />
            </div>
            <p className="text-gray-900 dark:text-gray-100 font-bold text-lg mb-1">No Recruiters Found</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Try adjusting your search or filters</p>
            <Button variant="outline" onClick={() => setSearchTerm('')}>Clear Search</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {(activeTab === 'our' ? ourRecruiters : users).map((user) => (
            <div
              key={user._id}
              onClick={() => setViewUser(user)}
              className="cursor-pointer bg-white dark:bg-[#002147] rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.02)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.15)] border border-gray-100/80 dark:border-[var(--placement-primary-border)]/35 hover:shadow-lg transition-all duration-300 flex flex-col justify-between group relative max-w-[280px] w-full mx-auto"
            >
              <div className="flex flex-col items-center text-center w-full">
                {/* Actions Button positioned absolutely */}
                {activeTab === 'our' && (
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === user._id ? null : user._id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-655 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-[#001f40]/55 transition-all"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {openDropdown === user._id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[var(--placement-primary-border)]/45 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] z-[100] py-1.5 overflow-visible text-left">
                        <button
                          onClick={() => {
                            navigate(`/college/edit-recruiter/${user._id}`);
                            setOpenDropdown(null);
                          }}
                          className="w-full px-4 py-2 text-left text-[12px] text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 flex items-center transition-colors"
                        >
                          <Edit className="w-3.5 h-3.5 mr-2.5" />
                          Edit Recruiter
                        </button>
                        <button
                          onClick={() => {
                            activeTab === 'our'
                              ? handleOurStatusChange(user._id, user.status === 'active' ? 'inactive' : 'active')
                              : handleStatusChange(user._id, user.status === 'active' ? 'inactive' : 'active');
                            setOpenDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-left text-[12px] flex items-center transition-colors ${user.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-[var(--placement-primary)] hover:bg-[var(--placement-primary-light)]'
                            }`}
                        >
                          {user.status === 'active' ? (
                            <><UserX className="w-3.5 h-3.5 mr-2.5" />Suspend Access</>
                          ) : (
                            <><UserCheck className="w-3.5 h-3.5 mr-2.5" />Activate Access</>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            activeTab === 'our' ? handleOurDelete(user._id) : handleDelete(user._id);
                            setOpenDropdown(null);
                          }}
                          className="w-full px-4 py-2 text-left text-[12px] text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 flex items-center transition-colors"
                        >
                          <X className="w-3.5 h-3.5 mr-2.5" />
                          Delete Recruiter
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Centered Logo */}
                <div className="w-16 h-16 rounded-2xl bg-white dark:bg-[#002147] border border-gray-150 dark:border-[var(--placement-primary-border)]/30 flex items-center justify-center overflow-hidden shrink-0 shadow-[0_4px_12px_-2px_rgba(0,0,0,0.05)] mb-4 mt-2">
                  {user.profileImage ? (
                    <img src={user.profileImage} className="w-full h-full object-cover rounded-xl" alt="Company Logo" />
                  ) : (
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-[#02d1a3] to-[#0bc3a0] flex items-center justify-center font-bold text-white text-lg shadow-sm">
                      {sanitizeInput((user.qualification || user.companyName)?.charAt(0)?.toUpperCase() || 'G')}
                    </div>
                  )}
                </div>

                {/* Centered Company Name */}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1 line-clamp-1">
                  {sanitizeInput(user.companyName || user.qualification || user.fullName || 'Company Name')}
                </h2>

                {/* Company ID + Recruiter ID badges */}
                {(user.companyId || user.recruiterId) && (
                  <div className="flex flex-wrap justify-center gap-1.5 mb-2">
                    {user.companyId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold bg-[#00152E] text-white tracking-wide">
                        {user.companyId}
                      </span>
                    )}
                    {user.recruiterId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold bg-[#1a3884] text-white tracking-wide">
                        {user.recruiterId}
                      </span>
                    )}
                  </div>
                )}

                {/* Compact Website */}
                {user.website && (
                  <a
                    href={user.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-[#4f7dff] hover:underline truncate w-full px-2"
                  >
                    <Globe className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{user.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}

                {/* Company Description */}
                {(user.aboutCompany || user.about || user.description) && (
                  <div className="mt-3 w-full px-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed text-center">
                      {sanitizeInput(user.aboutCompany || user.about || user.description)}
                    </p>
                  </div>
                )}
              </div>
              {/* Bottom Row removed */}
            </div>
          ))}
        </div>
      )}

      {/* ── Our Recruiter Modal ────────────────────────────────────────── */}
      {isOurModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-[#00152E] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 dark:border-[var(--placement-primary-border)]">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-[var(--placement-primary-border)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] rounded-lg"><Plus className="w-5 h-5 text-[var(--placement-primary)]" /></div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add Recruiter Partner</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Add a company to your college's recruiter list</p>
                </div>
              </div>
              <button type="button" onClick={() => setIsOurModalOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleOurSubmit}>
              <div className="p-6 space-y-5">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Company Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="relative w-14 h-14 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center overflow-hidden shrink-0 group">
                      {ourFormData.profileImage ? (
                        <><img src={ourFormData.profileImage} alt="Logo" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setOurFormData(p => ({ ...p, profileImage: '' }))}
                            className="absolute top-0.5 right-0.5 p-0.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100">
                            <X size={9} /></button></>
                      ) : (<Briefcase className="w-5 h-5 text-gray-400" />)}
                    </div>
                    <div>
                      <input type="file" ref={ourFileRef} accept="image/*" className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (!file.type.startsWith('image/')) { toast.error('Image files only'); return; }
                          if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
                          const reader = new FileReader();
                          reader.onloadend = () => setOurFormData(p => ({ ...p, profileImage: reader.result }));
                          reader.readAsDataURL(file);
                        }} />
                      <button type="button" onClick={() => ourFileRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-xl">
                        <Upload size={12} />Upload Logo</button>
                      <p className="text-[10px] text-gray-500 mt-1">PNG, JPG or SVG up to 2MB</p>
                    </div>
                  </div>
                </div>
                {/* Company Name */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Company / Organization *</label>
                  <Input name="companyName" value={ourFormData.companyName}
                    onChange={(e) => setOurFormData(p => ({ ...p, companyName: e.target.value }))}
                    placeholder="e.g. Google India" required
                    className="h-11 dark:bg-[#002147] dark:border-[var(--placement-primary-border)]/50 dark:text-white" />
                </div>
                {/* Website */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Website</label>
                  <Input type="url" name="website" value={ourFormData.website}
                    onChange={(e) => setOurFormData(p => ({ ...p, website: e.target.value }))}
                    placeholder="https://example.com"
                    className="h-11 dark:bg-[#002147] dark:border-[var(--placement-primary-border)]/50 dark:text-white" />
                </div>
              </div>
              <div className="flex gap-3 items-center justify-end p-6 border-t border-gray-100 dark:border-[var(--placement-primary-border)]/40 bg-gray-50/50 dark:bg-[#001f40]/40">
                <Button type="button" variant="outline" onClick={() => setIsOurModalOpen(false)}
                  className="px-6 h-11 border-gray-200 dark:border-[var(--placement-primary-border)]/50 dark:text-gray-300 dark:bg-transparent">
                  Cancel
                </Button>
                <Button type="submit" disabled={ourSaving}
                  className="px-8 h-11 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white shadow-lg border-none rounded-xl font-semibold">
                  {ourSaving ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Add Recruiter</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Smaart Recruiter legacy modal ────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-[#00152E] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 dark:border-[var(--placement-primary-border)] animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-[var(--placement-primary-border)]">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[var(--placement-primary)] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[var(--placement-primary-hover)]/50 rounded-xl transition-all border border-gray-200 dark:border-[var(--placement-primary-border)] group shadow-sm flex items-center justify-center shrink-0 mr-1"
                  title="Back"
                >
                  <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="p-2 bg-[#00152E]/5 dark:bg-[var(--placement-primary)]/20 rounded-lg">
                  <Plus className="w-6 h-6 text-[var(--placement-primary)]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Recruiter Partner</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Onboard a new hiring partner account</p>
                </div>
              </div>
              <button type="button" onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors text-gray-400 dark:text-gray-400 hidden sm:block">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col max-h-[85vh]">
              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(85vh-150px)] custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Logo Upload */}
                  <div className="md:col-span-2 space-y-2 border-b border-gray-100 dark:border-slate-800 pb-4">
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Company Logo</label>
                    <div className="flex items-center gap-4">
                      <div className="relative w-16 h-16 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center overflow-hidden shrink-0 shadow-inner group">
                        {formData.profileImage ? (
                          <>
                            <img src={formData.profileImage} alt="Logo" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, profileImage: '' }))}
                              className="absolute top-1 right-1 p-0.5 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors opacity-0 group-hover:opacity-100 shadow-md"
                            >
                              <X size={10} />
                            </button>
                          </>
                        ) : (
                          <Briefcase className="w-6 h-6 text-gray-400 dark:text-slate-600" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <input
                          type="file"
                          ref={modalFileRef}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (!file.type.startsWith('image/')) {
                                toast.error('Please upload an image file');
                                return;
                              }
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error('Image size must be less than 2MB');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setFormData(prev => ({ ...prev, profileImage: reader.result }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          accept="image/*"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => modalFileRef.current?.click()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-xl transition-all shadow-sm"
                        >
                          <Upload size={12} />
                          Upload Logo
                        </button>
                        <p className="text-[10px] text-gray-500">PNG, JPG, or SVG up to 2MB.</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Company / Organization *</label>
                    <Input
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleChange}
                      placeholder="e.g. Google India"
                      required
                      className="h-11 dark:bg-[#002147] dark:border-[var(--placement-primary-border)]/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Website</label>
                    <Input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://example.com"
                      autoComplete="off"
                      className="h-11 dark:bg-[#002147] dark:border-[var(--placement-primary-border)]/50 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-center justify-end p-6 border-t border-gray-100 dark:border-[var(--placement-primary-border)]/40 bg-gray-50/50 dark:bg-[#001f40]/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-6 h-11 border-gray-200 hover:bg-gray-50 dark:border-[var(--placement-primary-border)]/50 dark:hover:bg-[var(--placement-primary-hover)]/30 dark:text-gray-300 dark:bg-transparent justify-center"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="px-8 h-11 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white shadow-lg shadow-[var(--placement-primary-border)] border-none rounded-xl transition-all font-semibold"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Add Recruiter
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recruiter Details Modal */}
      {viewUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setViewUser(null)}>
          <div className="relative bg-white dark:bg-[#00152E] rounded-[1.5rem] shadow-2xl flex flex-col w-full max-w-[500px] overflow-hidden border border-slate-100 dark:border-emerald-600/20" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0 bg-white dark:bg-[#00152E]">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-50 dark:bg-[#002147] border border-slate-200/60 dark:border-slate-700/50 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                  {viewUser.profileImage ? (
                    <img src={viewUser.profileImage} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-6 h-6 text-slate-400 dark:text-slate-500" />
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    {sanitizeInput(viewUser.companyName || viewUser.qualification || viewUser.fullName || 'Company Name')}
                  </h2>
                  {/* Company ID + Recruiter ID badges */}
                  {(viewUser.companyId || viewUser.recruiterId) && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {viewUser.companyId && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#00152E] text-white tracking-wide">
                          {viewUser.companyId}
                        </span>
                      )}
                      {viewUser.recruiterId && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1a3884] text-white tracking-wide">
                          {viewUser.recruiterId}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewUser(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg transition-colors absolute top-4 right-4 bg-slate-50 dark:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Body */}
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(85vh-120px)] custom-scrollbar bg-white dark:bg-[#00152E]">
              
              {(viewUser.aboutCompany || viewUser.about || viewUser.description) && (
                <div className="space-y-2">
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                    About Company
                  </label>
                  <div className="p-4 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {sanitizeInput(viewUser.aboutCompany || viewUser.about || viewUser.description)}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {viewUser.website && (
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                      Website
                    </label>
                    <a href={viewUser.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4f7dff] hover:underline p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800 break-all">
                      <Globe className="w-4 h-4 shrink-0" />
                      {viewUser.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                
                {(viewUser.contactEmail || viewUser.email) && (
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                      Contact Mail
                    </label>
                    <a href={`mailto:${viewUser.contactEmail || viewUser.email}`} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800 break-all">
                      <Mail className="w-4 h-4 shrink-0 text-slate-400" />
                      {viewUser.contactEmail || viewUser.email}
                    </a>
                  </div>
                )}

                {(viewUser.contactPhone || viewUser.mobile) && (
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                      Phone Number
                    </label>
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                      <Phone className="w-4 h-4 shrink-0 text-slate-400" />
                      {viewUser.contactPhone || viewUser.mobile}
                    </div>
                  </div>
                )}

                {(viewUser.registrationNumber || viewUser.gstin) && (
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                      GSTIN / CIN
                    </label>
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                      <Briefcase className="w-4 h-4 shrink-0 text-slate-400" />
                      {viewUser.registrationNumber || viewUser.gstin}
                    </div>
                  </div>
                )}
              </div>

              {/* Branch IDs — shown if branches exist */}
              {viewUser.branches && viewUser.branches.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                    Branches ({viewUser.branches.length})
                  </label>
                  <div className="space-y-2">
                    {viewUser.branches.map((branch, idx) => (
                      <div key={idx} className="flex items-start justify-between p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{branch.branchName || `Branch ${idx + 1}`}</p>
                          {branch.branchId && (
                            <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#00152E] text-white tracking-wide">
                              {branch.branchId}
                            </span>
                          )}
                          {(branch.city || branch.state) && (
                            <p className="text-xs text-slate-500 mt-0.5">{[branch.city, branch.state].filter(Boolean).join(', ')}</p>
                          )}
                        </div>
                        <span className={`shrink-0 ml-2 text-[9px] font-bold px-2 py-0.5 rounded-full ${
                          branch.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'
                        }`}>{branch.status || 'active'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CollegeRecruiter;
